/**
 * SanctaCommerce — full-page conversational shopping surface.
 *
 * Theme-aware ambient background: light shell for the default site theme,
 * dark shell when `html.dark` is active. Renders `<SanctaChatbot />` in
 * `variant="page"` mode so the browser becomes the chat surface with all
 * of Slice 1's features (page awareness, inline blocks, cart preview,
 * Dupe Finder, SibaScore explainer).
 */
import React from "react";
import { motion } from "framer-motion";
import { Sparkles, ArrowLeft, BrainCircuit } from "lucide-react";
import { Link } from "react-router-dom";
import { SEO } from "@/components/shared/SEO";
import { SanctaChatbot } from "@/components/shared/SanctaChatbot";
import { useChatbotConfig, interpolate } from "@/hooks/useChatbotConfig";
import { SanctaMemoryDrawer } from "@/components/sancta/SanctaMemoryDrawer";
import { useAuth } from "@/contexts/AuthContext";

const SanctaCommerce: React.FC = () => {
  const cfg = useChatbotConfig();
  const { user } = useAuth();
  const [memOpen, setMemOpen] = React.useState(false);
  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-orange-50 via-white to-fuchsia-50 text-neutral-900 dark:from-slate-950 dark:via-slate-900 dark:to-fuchsia-950 dark:text-white">
      <SEO
        title="Sancta™ — Conversational shopping agent"
        description="Ask Sancta™ anything about products, prices or verified sellers on SociBaba. Multilingual AI concierge that answers in 12 languages, remembers your preferences, and drops you straight into a cart with the best SibaScore."
        path="/sancta"
        keywords={[
          "Sancta AI",
          "conversational commerce",
          "AI shopping assistant",
          "multilingual chatbot",
          "product Q&A",
          "SociBaba Sancta",
          "AI shopping agent India",
        ]}
      />
      {/* Ambient glow orbs — softer in light theme, punchy in dark */}
      <div aria-hidden className="pointer-events-none absolute top-0 left-1/4 h-96 w-96 rounded-full bg-fuchsia-200/60 blur-3xl dark:bg-fuchsia-500/20" />
      <div aria-hidden className="pointer-events-none absolute bottom-0 right-1/4 h-96 w-96 rounded-full bg-orange-200/60 blur-3xl dark:bg-orange-500/20" />
      <div aria-hidden className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[600px] w-[600px] rounded-full bg-cyan-200/40 blur-3xl animate-pulse dark:bg-cyan-500/10" style={{ animationDuration: "6s" }} />

      {/* Header */}
      <header className="relative z-10 border-b border-neutral-200/80 bg-white/70 backdrop-blur-md dark:border-white/10 dark:bg-slate-950/40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <Link
            to="/shop"
            data-testid="sancta-commerce-back"
            className="inline-flex items-center gap-1 text-sm text-neutral-600 hover:text-neutral-900 dark:text-slate-300 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Shop
          </Link>
          <div className="ml-auto flex items-center gap-2">
            {user && (
              <button
                onClick={() => setMemOpen(true)}
                data-testid="sancta-commerce-open-memory"
                className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-neutral-200 dark:border-white/15 text-neutral-700 dark:text-slate-200 hover:bg-neutral-100 dark:hover:bg-white/5"
                title="What Sancta remembers about you"
              >
                <BrainCircuit className="h-3.5 w-3.5 text-fuchsia-500" />
                Memory
              </button>
            )}
            <motion.div
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
              className="h-10 w-10 rounded-full grid place-items-center text-white"
              style={{ backgroundImage: `linear-gradient(135deg, ${cfg.accent_color}, #d946ef)` }}
            >
              <Sparkles className="h-5 w-5" />
            </motion.div>
            <div>
              <div className="font-display font-bold text-lg leading-tight" data-testid="sancta-commerce-name">
                {cfg.name} Commerce
              </div>
              <div className="text-xs text-neutral-500 dark:text-slate-400 leading-tight">
                {interpolate(cfg.tagline, cfg)}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Chat surface — inline the widget in page mode */}
      <div className="relative z-10 max-w-4xl mx-auto px-2 sm:px-6 pt-4 pb-8">
        <SanctaChatbot variant="page" />
      </div>

      <SanctaMemoryDrawer open={memOpen} onClose={() => setMemOpen(false)} />
    </div>
  );
};

export default SanctaCommerce;
